import { createCSSRuleFromTheme, Spinner } from '@fluentui/react-components';
import { jdLightTheme } from '@galaxy-react/core';
import { AuthAgent } from '@leapdev/auth-agent';
import { LicenseManager } from 'ag-grid-enterprise';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { init } from 'commandbar';
import { createRoot } from 'react-dom/client';
import App from './app/App';
import { environment, updateTimeZone } from './environment';
import { getFirmConfig } from './getInitialState';
import initialiseI18n from './i18n';
import './index.css';
import { registerAgGridModules } from './registerModule';
import { initSentry } from './sentry';
import { db } from './services/db';
import eventEmitter from './Shared/Utilities/eventEmitter';
initSentry();
init('ae8b03b1');

registerAgGridModules();

LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-079001}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{LEGAL_SOFTWARE_DEVELOPMENTS_PTY._LIMITED}_is_granted_a_{Multiple_Applications}_Developer_License_for_{14}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{16}_Production_Environments___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{19_June_2026}____[v3]_[01]_MTc4MTgyMzYwMDAwMA==76b4d3a5b148abd8e27252f4901722fa',
);
// login by jti

// replace Provider for fluentui
const presetCss = createCSSRuleFromTheme(':root', jdLightTheme);
const styleEl = document.createElement('style');
styleEl.appendChild(document.createTextNode(presetCss));
document.head.appendChild(styleEl);

axios.defaults.headers['Content-Type'] = 'application/json';
axios.interceptors.request.use((axiosConfig) => {
  const { headers, url } = axiosConfig;
  headers['Authorization'] = `Bearer ${AuthAgent.getAccessToken()}`;
  axiosConfig.url = url?.toLowerCase();
  return axiosConfig;
});
axios.defaults.validateStatus = (status) => {
  return (status >= 200 && status < 300) || status === 303 || status === 413;
};
axios.interceptors.response.use(
  async (response: AxiosResponse) => {
    return response;
  },
  async function (error: AxiosError) {
    const originalRequest = error.config;
    // @ts-ignore
    if (error?.response?.status === 401 && !originalRequest._retry) {
      // @ts-ignore
      // Avoid endless loops
      originalRequest._retry = true;
      const token = await AuthAgent.getRefreshedAccessToken();
      if (token) {
        originalRequest.headers['Authorization'] = `Bearer ${token}`;
      }
      return axios(originalRequest);
    }

    const message = error?.response?.data?.detail || error?.response?.data?.title;
    // show all error notification on test env
    if (message) {
      eventEmitter.emit('toast', { title: 'Request Error', message: message, type: 'error' });
    }
    return Promise.reject(error);
  },
);

AuthAgent.init({
  authHost: environment.leapAuth.oauthEndpoint,
  autoLogin: true,
  autoLogout: true,
  clientId: environment.leapAuth.clientId,
  scopes: ['*', 'offline_access'],
  autoRefreshToken: false,
  idleTimeoutInMinutes: 60,
  uniqueSession: true,
  hooks: {
    afterLogin: function afterLogin(token) {
      const root = createRoot(document.getElementById('root')!);
      root.render(
        <div
          style={{ height: '100vh', width: '100vw', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <Spinner />
        </div>,
      );

      Promise.all([getFirmConfig(), AuthAgent.userInfo()]).then(([firmConfig, userInfo]) => {
        initialiseI18n(userInfo.countryCode);
        if (userInfo.countryCode === 5) {
          updateTimeZone('Pacific/Auckland');
        }
        root.render(<App></App>);
      });
    },
    beforeLogout: function beforeLogout() {
      db.delete();
    },
  },
});
